import React, { useState } from 'react';
import { ProductOutput } from 'graphql/generated/hasura';
import ModalComponent from 'components/modal/modalComponent';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as DefaultTest } from 'assets/icons/testKits/testKit.svg';
import ButtonComponent from 'components/button/buttonComponent';
import { useAuth } from 'auth/context/AuthContext';
import { useGetProvidersByPatientIdQuery } from 'graphql/generated/remote-schema-hasura';

interface ProductDetailsModalProps extends Omit<ProductOutput, '__typename'> {
  onClose: () => void;
  addToCart: () => void;
  removeFromCart: () => void;
  hasSelectedProduct?: boolean;
  isLoadingCartData?: boolean;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  onClose,
  removeFromCart,
  isLoadingCartData,
  hasSelectedProduct = false,
  addToCart,
  image_url,
  title,
  short_description,
  specs,
  price,
}) => {
  const { user: loggedUser, selectedAccount } = useAuth();
  const { data } = useGetProvidersByPatientIdQuery({
    fetchPolicy: 'network-only',
    skip: !loggedUser?.uuid && !selectedAccount,
    variables: {
      patientCodexId: selectedAccount || loggedUser?.uuid,
    },
  });
  const hasProvider = !!data?.getProvidersByPatientId?.total;

  const [imageFailed, setImageFailed] = useState(false);

  const handleError = () => {
    if (image_url) {
      setImageFailed(true);
    }
  };

  const showFallback = !image_url || imageFailed;

  return (
    <ModalComponent isOpen title="Product details" onClose={onClose}>
      <div className="flex flex-col">
        <div className="flex flex-col pb-10 desktop:flex-row desktop:gap-10 gap-5">
          <div className="w-full m-auto rounded-10 overflow-hidden h-[280px] min-w-[320px]">
            {showFallback ? (
              <div className="flex items-center justify-center w-full h-full bg-white">
                <DefaultTest className="h-10 w-10 fill-charcoal-gray stroke-charcoal-gray" />
              </div>
            ) : (
              <img
                className={twMerge('object-cover object-center w-full h-full')}
                onError={handleError}
                src={image_url}
                alt="Test Photo"
              />
            )}
          </div>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <p className="text-neutral-800 font-semibold text-h3">{title}</p>
              <p className="text-med-gray-3 font-medium text-sm">
                {short_description}
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <p className="text-neutral-800 font-semibold text-base">
                Bulleted Specs
              </p>

              <div className="flex flex-col gap-1.5">
                <p className="text-sm">
                  <span className="mr-1 text-neutral-800 font-semibold">
                    Contents:
                  </span>
                  <span className="text-med-gray-3 font-medium">
                    {specs?.contents}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="mr-1 text-neutral-800 font-semibold">
                    Made in:
                  </span>
                  <span className="text-med-gray-3 font-medium">
                    {specs?.made_in}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="mr-1 text-neutral-800 font-semibold">
                    Period after opening:
                  </span>
                  <span className="text-med-gray-3 font-medium">
                    {specs?.expiration_period}{' '}
                    {specs?.expiration_period === 1 ? 'month' : 'months'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          className={twMerge(
            'flex flex-col desktop:-mx-10 -mx-7 px-7 -mb-10 py-30 rounded-b-10 gap-5 justify-between bg-white desktop:flex-row-reverse desktop:px-10',
          )}
        >
          <div
            className={twMerge(
              'flex flex-row-reverse h-[41px] items-center gap-5 desktop:gap-30',
            )}
          >
            <div className={twMerge(!hasProvider && 'hidden')}>
              {!hasSelectedProduct ? (
                <ButtonComponent
                  disabled={isLoadingCartData}
                  showSpinner={isLoadingCartData}
                  testID="add-to-cart"
                  onClick={addToCart}
                >
                  {'Add to cart'}
                </ButtonComponent>
              ) : (
                <ButtonComponent
                  testID="remove-from-cart"
                  onClick={removeFromCart}
                  type={'underline'}
                  disabled={isLoadingCartData}
                  showSpinner={isLoadingCartData}
                  containerClassName="py-[9.5px]"
                >
                  {'Added to cart'}
                </ButtonComponent>
              )}
            </div>
            <div className="font-semibold text-h4 text-neutral-800">
              Price: ${price}
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  );
};

export default ProductDetailsModal;
